/**
 * SELECT COMPONENT
 * Composant select unifié et réutilisable avec style cohérent
 */

import { forwardRef } from 'react';
import type { SelectHTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  /**
   * Variante du select (par défaut: 'default')
   * - default: Style standard pour formulaires
   * - inline: Style pour édition inline dans les tableaux
   */
  variant?: 'default' | 'inline';

  /**
   * Options à afficher dans le select
   */
  options?: Array<{ value: string; label: string }>;

  /**
   * Placeholder option (ajouté en premier si fourni)
   */
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, variant = 'default', options, placeholder, children, ...props }, ref) => {
    const baseStyles =
      'h-9 rounded-md border border-zinc-200 bg-white text-sm outline-none transition-colors focus-visible:border-zinc-900 focus-visible:ring-2 focus-visible:ring-zinc-900 disabled:cursor-not-allowed disabled:opacity-50';

    const variantStyles = {
      default: 'px-3 py-2 shadow-xs w-full',
      inline: 'px-3 py-2 shadow-sm hover:bg-zinc-50',
    };

    return (
      <select ref={ref} className={cn(baseStyles, variantStyles[variant], className)} {...props}>
        {placeholder && (
          <option value="" disabled={props.required}>
            {placeholder}
          </option>
        )}
        {options
          ? options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))
          : children}
      </select>
    );
  }
);

Select.displayName = 'Select';
